export type Vfs = ReadonlyMap<string, string>;

function normalize(p: string): string {
  if (!p.startsWith("/")) throw new Error(`vfs paths must be absolute, got: ${p}`);
  const parts = p.split("/").filter((s) => s.length > 0);
  const out: string[] = [];
  for (const part of parts) {
    if (part === ".") continue;
    if (part === "..") {
      out.pop();
      continue;
    }
    out.push(part);
  }
  return "/" + out.join("/");
}

export function vfsJoin(...parts: string[]): string {
  if (parts.length === 0) return "/";
  const joined = parts
    .map((p, i) => (i === 0 ? p : p.replace(/^\/+/, "")))
    .filter((p) => p.length > 0)
    .join("/");
  return normalize(joined.startsWith("/") ? joined : "/" + joined);
}

export function vfsResolve(base: string, rel: string): string {
  if (rel.startsWith("/")) return normalize(rel);
  return vfsJoin(base, rel);
}

export function vfsDirname(p: string): string {
  const n = normalize(p);
  if (n === "/") return "/";
  const idx = n.lastIndexOf("/");
  return idx <= 0 ? "/" : n.slice(0, idx);
}

export function vfsBasename(p: string): string {
  const n = normalize(p);
  const idx = n.lastIndexOf("/");
  return idx < 0 ? n : n.slice(idx + 1);
}

export function vfsRelative(from: string, to: string): string {
  const a = normalize(from).split("/").filter(Boolean);
  const b = normalize(to).split("/").filter(Boolean);
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  const up = new Array(a.length - i).fill("..");
  const down = b.slice(i);
  return [...up, ...down].join("/");
}

export function vfsIsInside(child: string, parent: string): boolean {
  const c = normalize(child);
  const p = normalize(parent);
  if (c === p) return true;
  return c.startsWith(p === "/" ? "/" : p + "/");
}

/** THE markdown-path law: is `path` a markdown document? `.md`/`.markdown`,
 *  case-insensitive (file managers treat extensions case-insensitively, and
 *  the nav already classified `X.MD` as a document — every surface must
 *  agree). Single definition — package discovery, the app shell's doc-tab
 *  routing (`specForPath`), the nav, the LSP bridge, and the diff views all
 *  share it; a divergent inline `endsWith(".md")` reintroduces the
 *  "document open in Monaco" class (bugloop r23–r24). */
export function isMarkdownPath(path: string): boolean {
  return /\.(md|markdown)$/i.test(path);
}
